import React from 'react'
import { Link } from 'react-router-dom'
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons'
import {
  PixelDate,
  PixelTable,
  PixelTooltip,
  PixelIcon,
  DcDiv,
} from 'pixel-base'
import styled from 'styled-components'

interface InventoryHistoryItem {
  product_id: number
  created_at: {
    date: string
  }
  on_hand: number | null
  committed: number | null
  available: number | null
  unavailable: number | null
  quantity: number | null
  reason: string | null
  notes: string | null
  is_current?: number | null
}

interface InventoryHistoryTableProps {
  data: InventoryHistoryItem[]
  loading?: boolean
  onOrderClick?: (orderId: string) => void
  onSort?: (column: 'on_hand' | 'committed') => void
  sortBy?: 'on_hand' | 'committed' | null
  sortOrder?: 'asc' | 'desc'
}

const AvailableQty = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CurrentChip = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  white-space: nowrap;
  margin-left: 4px;
`

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #007bff;
  }
`

const SortIcon = styled.span<{ active: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.active ? '#007bff' : '#6c757d')};
  font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
`

const InventoryHistoryTable: React.FC<InventoryHistoryTableProps> = ({
  data,
  loading = false,
  onOrderClick,
  onSort,
  sortBy,
  sortOrder,
}) => {
  // Utility function to clean malformed date strings
  const cleanDateString = (dateString: string): string => {
    if (!dateString) return ''

    // Remove duplicate timezone information (e.g., +05:00+05:00)
    const cleaned = dateString.replace(/([+-]\d{2}:\d{2})\1/, '$1')

    // Validate the date
    const date = new Date(cleaned)
    return isNaN(date.getTime()) ? '' : cleaned
  }

  const columns = [
    {
      dataKey: 'created_at',
      name: 'Date',
      sortable: true,
      width: '200px',
      minWidth: '180px',
      selector: (row: InventoryHistoryItem) => {
        const cleanedDate = cleanDateString(row.created_at?.date || '')
        return cleanedDate ? (
          <PixelDate
            className="date"
            value={cleanedDate}
            format={'dateWithTime'}
          />
        ) : (
          '--'
        )
      },
    },
    {
      dataKey: 'quantity',
      name: 'TXN Quantity',
      sortable: true,
      width: '120px',
      minWidth: '100px',
      selector: (row: InventoryHistoryItem) =>
        row.quantity !== null ? row.quantity : '--',
    },
    {
      dataKey: 'notes',
      name: 'Notes',
      sortable: true,
      width: '385px',
      minWidth: '300px',
      selector: (row: InventoryHistoryItem) => {
        if (!row.notes) return '--'

        const parts = row.notes.split('#')
        if (parts.length < 2) return row.notes

        const [noteText, orderId] = parts
        return (
          <PixelTooltip tooltipText={row.notes}>
            <span>
              {noteText.trim()}&nbsp;
              <Link
                onClick={() => {
                  if (onOrderClick) {
                    onOrderClick(orderId.trim())
                  }
                }}
                style={{ cursor: 'pointer', color: '#007bff' }}
                to={`/orders?order_id=${orderId.trim()}`}
              >
                {`(#${orderId.trim()})`}
              </Link>
            </span>
          </PixelTooltip>
        )
      },
    },
    {
      dataKey: 'committed',
      name: onSort ? (
        <SortableHeader onClick={() => onSort('committed')}>
          Committed
          <SortIcon active={sortBy === 'committed'}>
            {sortBy === 'committed' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
          </SortIcon>
        </SortableHeader>
      ) : (
        'Committed'
      ),
      sortable: true,
      width: '140px',
      minWidth: '120px',
      selector: (row: InventoryHistoryItem) => (
        <AvailableQty>
          {row.committed !== null ? row.committed : '--'}
          {row.committed !== null && row.committed > 0 ? (
            <PixelIcon
              style={{
                cursor: 'pointer',
              }}
              fontSize="14px"
              color={'green'}
              icon={faArrowUp}
            />
          ) : row.committed !== null && row.committed < 0 ? (
            <PixelIcon color={'red'} icon={faArrowDown} />
          ) : null}
        </AvailableQty>
      ),
    },

    {
      dataKey: 'available',
      name: 'Available',
      sortable: true,
      width: '140px',
      minWidth: '120px',
      selector: (row: InventoryHistoryItem) => (
        <AvailableQty>
          {row.available !== null ? row.available : '--'}
          {row.available !== null && row.available > 0 ? (
            <PixelIcon
              style={{
                cursor: 'pointer',
              }}
              fontSize="14px"
              color={'green'}
              icon={faArrowUp}
            />
          ) : row.available !== null && row.available < 0 ? (
            <PixelIcon color={'red'} icon={faArrowDown} />
          ) : null}
        </AvailableQty>
      ),
    },
    {
      dataKey: 'on_hand',
      name: onSort ? (
        <SortableHeader onClick={() => onSort('on_hand')}>
          On Hand
          <SortIcon active={sortBy === 'on_hand'}>
            {sortBy === 'on_hand' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
          </SortIcon>
        </SortableHeader>
      ) : (
        'On Hand'
      ),
      sortable: true,
      width: '120px',
      minWidth: '100px',
      selector: (row: InventoryHistoryItem) =>
        row.on_hand !== null ? row.on_hand : '--',
    },
    {
      dataKey: 'reason',
      name: 'Reason',
      sortable: true,
      width: '150px',
      minWidth: '130px',
      selector: (row: InventoryHistoryItem) => (
        <AvailableQty>
          {row.reason ? row.reason : '--'}
          {row.is_current === 1 && <CurrentChip>CURRENT</CurrentChip>}
        </AvailableQty>
      ),
    },
  ]

  return (
    <DcDiv>
      <PixelTable
        columns={columns}
        data={data || []}
        isSearchable={true}
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 480px)"
        progressPending={loading}
      />
    </DcDiv>
  )
}

export default InventoryHistoryTable
